import React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  alpha,
  CircularProgress,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { P2PWEBSERVICE, WEBSERVICE, showToast } from "../../CoreDataService";
import P2PNavbar from "./P2PNavbar";
import { useSelector } from "react-redux";
import { OutlinedButton } from "../../Common/Buttons";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

export default function OrdersByAdPage() {
  const { btncolor } = useSelector((state) => state);
  const theme = localStorage.getItem("theme");
  const { adId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ordersByAdList, setOrdersByAdList] = useState([]);

  useEffect(() => {
    const fetchOrdersByAd = async () => {
      try {
        setLoading(true);
        const payload = {
          uuid: localStorage.getItem("uuid"),
          adId,
        };

        const { data: response } = await axios.post(
          `${P2PWEBSERVICE}/getOrdersByAd`,
          JSON.stringify(payload),
          {
            headers: {
              "Content-Type": "application/json",
              authorization: `BEARER ${localStorage.getItem("access_token")}`,
            },
          }
        );

        if (
          response.error.error_data === 0 &&
          response.orderDetails?.length > 0
        ) {
          setOrdersByAdList(
            response.orderDetails.map((order) => ({
              uuid: order.uuid,
              orderType: order.orderType,
              coin: order.coin,
              orderDate: order.orderDate,
              fiatAmount: order.fiatAmount,
              totalFiatPrice: order.totalFiatPrice,
              price: order.price,
              cryptoAmount: order.cryptoAmount,
              counterParty: order.counterParty,
              status: order.status,
              operation: order.operation,
              fromDate: order.fromDate,
              toDate: order.toDate,
              offset: order.offset,
              limit: order.limit,
              orderStatus: order.orderStatus,
              isAdmin: order.isAdmin,
              adId: order.adId,
              orderId: order.orderId,
              fiatPrice: order.fiatPrice,
              totalAmount: order.totalAmount,
              crypto: order.crypto,
              fiat: order.fiat,
              paymentMethod: order.paymentMethod,
              nickName: order.nickName,
              adType: order.adType,
              counterPartyUuid: order.counterPartyUuid,
            }))
          );
        } else {
          setOrdersByAdList([]);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        showToast("error", "Something went wrong fetching orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrdersByAd();
  }, [adId]);

  const getStatusColor = (status) => {
    if (status === "COMPLETED") return "green";
    if (status === "CANCELLED") return "red";
    return "inherit";
  };

  const goToOrderDetails = (order) => {
    localStorage.setItem("previousAdId", order.adId);
    localStorage.setItem("previousOrderId", order.orderId);
    localStorage.setItem("orderCreationTime", order.orderDate);
    localStorage.setItem("counterPartyUuid", order.counterPartyUuid);
    localStorage.setItem("chatUserName", order.nickName);
    localStorage.setItem("orderfiat", order.fiat);
    localStorage.setItem("orderCrypto", order.crypto);

    if (order.adType.toLowerCase() === "buy") {
      localStorage.setItem("previousPageBuyprice", order.cryptoAmount);
      localStorage.setItem("previousPageBuyAmount", order.fiatPrice);
      localStorage.setItem("previousPageBuyTotalAmount", order.totalFiatPrice);
      navigate("/p2p/sell-order-details");
    } else {
      localStorage.setItem("previousPageBuyprice", order.fiatPrice);
      localStorage.setItem("previousPageBuyAmount", order.cryptoAmount);
      localStorage.setItem("previousPageBuyTotalAmount", order.totalFiatPrice);
      navigate("/p2p/order-details");
    }
  };

  const renderStatusChip = (status) => {
    const colorMap = {
      PROCESSING: { color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
      PENDING: { color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
      COMPLETED: { color: "#22c55e", bg: "rgba(34,197,94,0.1)" },
      CANCELLED: { color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
    };
    const style = colorMap[status] || { color: "var(--text-color)", bg: "rgba(125,125,125,0.1)" };
    return (
      <Box
        sx={{
          display: "inline-flex",
          px: 1.25,
          py: 0.35,
          borderRadius: "6px",
          backgroundColor: style.bg,
          color: style.color,
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.02em",
        }}
      >
        {status}
      </Box>
    );
  };

  const renderOrderType = (type) => (
    <Box
      sx={{
        display: "inline-flex",
        px: 1.25,
        py: 0.35,
        borderRadius: "6px",
        backgroundColor:
          type.toUpperCase() === "BUY"
            ? "rgba(34,197,94,0.1)"
            : "rgba(239,68,68,0.1)",
        color: type.toUpperCase() === "BUY" ? "#22c55e" : "#ef4444",
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        mb: 0.5,
      }}
    >
      {type}
    </Box>
  );

  return (
    <>
      <P2PNavbar />
      <Box sx={pageWrapper}>
        {/* Hero Banner */}
        <Box
          sx={{
            ...heroBanner,
            background: `linear-gradient(135deg, ${alpha(
              btncolor || "#1976d2",
              0.95
            )} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
          }}
        >
          <Box sx={heroContent}>
            <Box sx={heroIconBadge}>
              <ListAltOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
            </Box>
            <Box>
              <Typography sx={heroTitle}>Orders for Ad #{adId}</Typography>
              <Typography sx={heroSubtitle}>
                View all orders associated with this advertisement
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Main Card */}
        <Box sx={mainCard}>
          {loading ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress sx={{ color: btncolor }} />
            </Box>
          ) : (
            <TableContainer sx={modernTableContainer}>
              <Table>
                <TableHead>
                  <TableRow sx={tableHeadRow}>
                    {[
                      "Type / Asset",
                      "Fiat Amount",
                      "Crypto Amount",
                      "Order Date",
                      "Counterparty",
                      "Status",
                      "Actions",
                    ].map((h) => (
                      <TableCell key={h} sx={tableHeadCell}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersByAdList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ borderBottom: 0 }}>
                        <Box sx={{ py: 7, textAlign: "center" }}>
                          <InboxOutlinedIcon
                            sx={{
                              fontSize: 48,
                              color: "var(--text-color)",
                              opacity: 0.2,
                              mb: 1.5,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "0.9rem",
                              color: "var(--text-color)",
                              opacity: 0.5,
                            }}
                          >
                            No orders found for this ad
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ordersByAdList.map((order, index) => (
                      <TableRow key={index} sx={tableBodyRow}>
                        <TableCell sx={tableBodyCell}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                            {renderOrderType(order.adType)}
                            <Typography
                              sx={{
                                fontSize: "0.78rem",
                                color: "var(--text-color)",
                                opacity: 0.6,
                              }}
                            >
                              {order.crypto} / {order.fiat}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={tableBodyCell}>
                          {Number(order.totalFiatPrice)?.toFixed(6)}{" "}
                          <span style={{ opacity: 0.5, fontSize: "0.75rem" }}>
                            {order.fiat}
                          </span>
                        </TableCell>
                        <TableCell sx={tableBodyCell}>
                          {Number(order.cryptoAmount)?.toFixed(6)}{" "}
                          <span style={{ opacity: 0.5, fontSize: "0.75rem" }}>
                            {order.crypto}
                          </span>
                        </TableCell>
                        <TableCell sx={tableBodyCell}>
                          <Typography sx={{ fontSize: "0.82rem", color: "var(--text-color)" }}>
                            {order.orderDate}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodyCell}>
                          {order.nickName}
                        </TableCell>
                        <TableCell sx={tableBodyCell}>
                          {renderStatusChip(order.status)}
                        </TableCell>
                        <TableCell sx={tableBodyCell}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography
                              sx={{ fontSize: "0.75rem", color: "var(--text-color)", opacity: 0.55 }}
                            >
                              #{order.orderId}
                            </Typography>
                            <OutlinedButton
                              onClick={() => goToOrderDetails(order)}
                              size="small"
                              variant="outlined"
                              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.78rem" }}
                            >
                              Go To Details
                            </OutlinedButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const modernTableContainer = {
  borderRadius: 0,
  border: "none",
  boxShadow: "none",
  backgroundColor: "var(--body-color-less)",
};

const tableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const tableHeadCell = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: "var(--text-color)",
  opacity: 0.6,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  py: 1.75,
  px: 2,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  whiteSpace: "nowrap",
};

const tableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "rgba(125,125,125,0.04)" },
  "&:last-child td": { borderBottom: 0 },
};

const tableBodyCell = {
  py: 1.65,
  px: 2,
  fontSize: "0.85rem",
  color: "var(--text-color)",
  borderBottom: "1px solid rgba(125,125,125,0.08)",
};
